/* eslint-disable no-console */
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

const LOG_STYLES = {
  DEBUG: {
    label: '%c[DEBUG]',
    style: 'background: #2d3436; color: #74b9ff; padding: 2px 4px; border-radius: 2px; font-weight: bold;'
  },
  INFO: {
    label: '%c[INFO]',
    style: 'background: #2d3436; color: #00b894; padding: 2px 4px; border-radius: 2px; font-weight: bold;'
  },
  WARN: {
    label: '%c[WARN]',
    style: 'background: #2d3436; color: #ffeaa7; padding: 2px 4px; border-radius: 2px; font-weight: bold;'
  },
  ERROR: {
    label: '%c[ERROR]',
    style: 'background: #2d3436; color: #ff7675; padding: 2px 4px; border-radius: 2px; font-weight: bold;'
  }
};

class Logger {
  constructor() {
    // Check for log level from localStorage or environment
    const storedLevel = localStorage.getItem('logLevel');
    const urlParams = new URLSearchParams(window.location.search);
    const urlLevel = urlParams.get('logLevel');

    // Priority: URL param > localStorage > environment default
    if (urlLevel && LOG_LEVELS[urlLevel.toUpperCase()] !== undefined) {
      this.level = LOG_LEVELS[urlLevel.toUpperCase()];
      localStorage.setItem('logLevel', urlLevel.toUpperCase());
    } else if (storedLevel && LOG_LEVELS[storedLevel] !== undefined) {
      this.level = LOG_LEVELS[storedLevel];
    } else {
      // Default to WARN level to reduce noise (only warnings and errors)
      this.level = import.meta.env.DEV ? LOG_LEVELS.WARN : LOG_LEVELS.ERROR;
    }

    // Note: We use console methods directly (not stored) so that the
    // consoleInterceptor can capture logger output for error tracking

    // Bind log methods so they survive being passed around unbound
    // (e.g. `const log = cond ? logger.error : logger.warn`) — an unbound
    // call makes `this.level` throw and crashes the caller instead of logging
    this.debug = this.debug.bind(this);
    this.info = this.info.bind(this);
    this.warn = this.warn.bind(this);
    this.error = this.error.bind(this);
  }

  setLevel(level) {
    const upperLevel = typeof level === 'string' ? level.toUpperCase() : level;
    if (LOG_LEVELS[upperLevel] !== undefined) {
      this.level = LOG_LEVELS[upperLevel];
      localStorage.setItem('logLevel', upperLevel);
      console.log(`%cLog level set to: ${upperLevel}`, 'color: #0984e3; font-weight: bold;');
    } else {
      console.warn('Invalid log level. Valid levels are:', Object.keys(LOG_LEVELS).join(', '));
    }
  }
  
  getCurrentLevel() {
    const levelName = Object.keys(LOG_LEVELS).find(key => LOG_LEVELS[key] === this.level);
    return levelName;
  }
  
  // Helper methods for quick level changes
  silence() {
    this.setLevel('NONE');
  }
  
  verbose() {
    this.setLevel('DEBUG');
  }
  
  normal() {
    this.setLevel('WARN');
  }

  _formatArgs(level, args) {
    const style = LOG_STYLES[level];
    const timestamp = new Date().toISOString();
    return [
      `%c[${timestamp}] ${style.label}`,
      'color: #636e72; font-style: italic;',
      style.style,
      ...args
    ];
  }

  debug(...args) {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.debug(...this._formatArgs('DEBUG', args));
    }
  }

  info(...args) {
    if (this.level <= LOG_LEVELS.INFO) {
      console.info(...this._formatArgs('INFO', args));
    }
  }

  warn(...args) {
    if (this.level <= LOG_LEVELS.WARN) {
      console.warn(...this._formatArgs('WARN', args));
    }
  }

  error(...args) {
    if (this.level <= LOG_LEVELS.ERROR) {
      console.error(...this._formatArgs('ERROR', args));
    }
  }

  // Log a Firestore real-time listener (onSnapshot) error at the right level.
  // permission-denied is almost always a benign teardown race — onSnapshot
  // fires one last error as the auth token is revoked on sign-out, before
  // onUnmounted unsubscribes; 'unavailable'/'internal'/'cancelled' are
  // transient backend/teardown blips. Those log at WARN (kept out of the
  // persisted error log); any other code is a genuine error.
  firestoreListenerError(prefix, error, context = {}) {
    const code = error?.code;
    const benign =
      code === 'permission-denied' ||
      code === 'unavailable' ||
      code === 'internal' ||
      code === 'cancelled';
    const payload = { error: error?.message || String(error), code, ...context };
    if (benign) {
      this.warn(`${prefix} (benign listener teardown/transient)`, payload);
    } else {
      this.error(prefix, payload);
    }
  }

  // Group logging methods with colors
  group(label) {
    console.group(`%c${label}`, 'color: #0984e3; font-weight: bold;');
  }

  groupEnd() {
    console.groupEnd();
  }

  // Special method for component lifecycle logging
  lifecycle(component, hook, ...args) {
    this.debug(
      `%c[${component}]%c ${hook}`,
      'color: #00cec9; font-weight: bold;',
      'color: #636e72;',
      ...args
    );
  }

  // Special method for API calls
  api(method, endpoint, ...args) {
    this.info(
      `%c[API]%c ${method} ${endpoint}`,
      'color: #6c5ce7; font-weight: bold;',
      'color: #636e72;',
      ...args
    );
  }
}

export const logger = new Logger();

// Expose logger to window for easy console access
if (import.meta.env.DEV) {
  window.logger = logger;
  
  // Log initial state and instructions
  console.log(
    '%c🔧 Logger Controls Available',
    'background: #2d3436; color: #00b894; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
  );
  console.log(`Current log level: ${logger.getCurrentLevel()}`);
  console.log('Commands:');
  console.log('  logger.silence()     - Turn off all logs');
  console.log('  logger.normal()      - Show only warnings and errors');
  console.log('  logger.verbose()     - Show all logs including debug');
  console.log('  logger.setLevel("INFO")  - Set specific level (DEBUG, INFO, WARN, ERROR, NONE)');
  console.log('  logger.getCurrentLevel() - Check current level');
  console.log('Or add ?logLevel=WARN to URL to set level');
} 